import { ref, inject, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import lodashReverse from "lodash-es/reverse";
import BatchSaveQueue from "@logic/BatchSaveQueue";
import { useToastsStore } from "@stores/toasts";
import { MetricSender } from "@helpers/MetricSender";
import { MAX_ASSETS_PER_GROUP } from "@contracts/photosPageData";

export interface PhotoGroupUploadOptions {
  /** Names the localStorage entry holding ids that are uploaded but not saved yet. Must be the same on every visit. */
  storageKey: string;
  /** Saves a batch of uploaded asset ids to the group, newest first. Throwing makes the queue retry the batch. */
  saveFunction: (assetIds: Array<string>) => Promise<void>;
  /** Assets the group holds right now, for the per-group limit. */
  getCurrentAssetCount: () => number;
  /** What the page calls a photo group, for the limit warning. */
  groupNoun: "group" | "album";
  /** Appended to the limit log line (ex. customer and group ids). */
  getLogContext: () => string;
}

/**
 * Upload bookkeeping for one photo group, to pair with an UploadPhotoForm:
 *  - uploaded asset ids are saved to the group in small batches (BatchSaveQueue), and
 *  - isUploadingInProgress stays true until the files AND those saves are done.
 * Ported from PhotosSpoiler.vue, which still carries its own copy.
 */
export function usePhotoGroupUpload(options: PhotoGroupUploadOptions) {
  const route = useRoute();
  const toastsStore = useToastsStore();
  const globalRemoteLogger = inject("globalRemoteLogger") as any;

  // This limit is for uploading (not rendering)
  // Can be overridden by route query param for testing
  const maxAssetsPerGroup = ref<number>(MAX_ASSETS_PER_GROUP);

  // Tracks if an upload has started
  const isUploadingInProgress = ref<boolean>(false);
  // Tracks whether the actual image file uploads have completed.
  // Both of these are needed because the uploads and batch save can happen in parallel
  // and it could seem like everything is "done" even when it's not.
  const areFileUploadsComplete = ref<boolean>(true);

  const batchSaveQueue = new BatchSaveQueue({
    saveFn: async idsArray => {
      // The queue holds ids oldest first; the group lists its newest asset first.
      await options.saveFunction(lodashReverse(idsArray));
    },
    storageKey: options.storageKey,
    batchSize: 5,
    maxWaitMs: 2000,
    // eslint-disable-next-line no-console
    onLog: msg => console.info(`PhotoBatchSave: ${msg}`),
  });

  function handleUploadingStarted() {
    isUploadingInProgress.value = true;
    areFileUploadsComplete.value = false;
  }

  function handleAssetUploaded(assetId: string) {
    batchSaveQueue.enqueue(assetId);
  }

  /**
   * Wire to UploadPhotoForm's uploadProgressChange. The form reports "not in progress" exactly once per run, after
   * every file has finished or failed. Its "error" event is NOT used for this: it fires for a single failed file
   * while the rest are still uploading, and treating it as the end would let the page act as if the run was over
   * (ex. filter the group off screen, which unmounts the form and aborts what is left).
   */
  function handleUploadProgressChange(info: { isUploadInProgress: boolean }) {
    if (info.isUploadInProgress) {
      handleUploadingStarted();
      return;
    }
    // Mark that file uploads are done
    areFileUploadsComplete.value = true;
    // Only mark upload as complete if batch save queue is also empty
    // This ensures we stay in "uploading" state until all saves are flushed
    if (!batchSaveQueue.hasPendingItems()) {
      isUploadingInProgress.value = false;
    }
    // Otherwise, the periodic check will set it false when queue empties
  }

  // Periodically check if we can complete the upload state
  // This handles the case where file uploads finish but batch save queue still has items
  let intervalId: ReturnType<typeof setInterval> | undefined;

  onMounted(() => {
    intervalId = setInterval(() => {
      // Only mark complete when BOTH file uploads are done AND batch queue is empty
      // This prevents the bug where an empty queue at upload START was incorrectly treated as "done"
      if (isUploadingInProgress.value && areFileUploadsComplete.value && !batchSaveQueue.hasPendingItems()) {
        isUploadingInProgress.value = false;
      }
    }, 1000);

    // Check for maxupload query param and apply if it reduces the limit
    const maxUploadParam = route.query.maxupload;
    if (maxUploadParam) {
      const maxUploadValue = parseInt(maxUploadParam as string, 10);
      if (!isNaN(maxUploadValue) && maxUploadValue < maxAssetsPerGroup.value) {
        maxAssetsPerGroup.value = maxUploadValue;
      }
    }
  });

  onBeforeUnmount(() => {
    clearInterval(intervalId);
    batchSaveQueue.dispose();
  });

  /** For UploadPhotoForm's beforeUploadCheck. Returns false (and tells the user) when the files won't fit. */
  function checkAssetLimitBeforeUpload(newFileCount: number): boolean {
    const currentAssetCount = options.getCurrentAssetCount();
    const totalAfterUpload = currentAssetCount + newFileCount;

    if (totalAfterUpload > maxAssetsPerGroup.value) {
      // Warn and log
      toastsStore.warn({
        message: `Max ${maxAssetsPerGroup.value} files per ${options.groupNoun}`,
        details: `Adding ${newFileCount} to ${currentAssetCount} existing files will exceed the current limit of ${maxAssetsPerGroup.value}.\nPlease split your upload into multiple ${options.groupNoun}s.`,
        expiresInMs: 30000,
      });
      globalRemoteLogger.info(`PhotoGallery AssetLimitExceeded: ${options.getLogContext()}, adding=${newFileCount}, currentCount=${currentAssetCount}, limit=${maxAssetsPerGroup.value}`, false, false);
      MetricSender.sendMetric("PhotoGallery-AssetLimitExceeded", { currentCount: currentAssetCount, adding: newFileCount, limit: maxAssetsPerGroup.value });

      return false; // Abort upload
    }
    // Upload can proceed
    return true;
  }

  return {
    isUploadingInProgress,
    areFileUploadsComplete,
    maxAssetsPerGroup,
    handleUploadingStarted,
    handleAssetUploaded,
    handleUploadProgressChange,
    checkAssetLimitBeforeUpload,
  };
}
